import HeaderDashboard from "../../Components/HeaderDashboard";
import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  getBookingDetailById,
  hotelBookHold,
  retrieveHotelBooking,
} from "../../Services/HotelSearchApi";
import HotelEmailTicket from "./HotelEmailTicket";
import SmsHotelTicket from "./SmsHotelTicket";
import { CollectPayment } from "../../Services/API";
import { AuthContext } from "../../Routes/AuthProvider";

const TransactionsDetails = () => {
  const { userName } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Using 'refernceno' to match the key used in AllTransactions.js redirection
  const referenceNo =
    queryParams.get("referenceno") ||
    queryParams.get("referenceNo") ||
    queryParams.get("ReferenceNo") ||
    queryParams.get("bookingRefId");

  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalStatus, setModalStatus] = useState("");

  const hasCheckedStatusRef = useRef(false);

  useEffect(() => {
    if (referenceNo) {
      hasCheckedStatusRef.current = false;
      fetchHotelBookingDetails(referenceNo);
    } else {
      console.warn("Missing referenceNo");
    }
  }, [referenceNo]);

  const fetchHotelBookingDetails = async (refId) => {
    try {
      setLoading(true);
      setError("");
      let response = await getBookingDetailById(refId);
      console.log("Hotel Booking Detail Response:", response);

      // Defensively handle array responses
      if (Array.isArray(response) && response.length > 0) {
        response = response[0];
      }

      // Defensively handle both wrapped { booking: { ... } } and direct booking object { ... }
      let details = null;
      if (response && response.booking) {
        details = response.booking;
      } else if (response && response.Booking) {
        details = response.Booking;
      } else if (response && response.pkid) {
        details = response;
      }

      if (details) {
        setBookingDetails(details);

        // Auto trigger modal / status retrieve for pending/non-final statuses
        const status = details.bookingStatus?.toUpperCase();
        const pendingStatuses = [
          "CANCELLATION_PENDING",
          "PENDING",
          "PAYMENT_PENDING",
          "PAYMENT_SUCCESS",
          "IN_PROGRESS",
          "IN_PAYMENT",
          "REQUESTED",
          "APPROVE_PENDING"
        ];

        if (pendingStatuses.includes(status) && !hasCheckedStatusRef.current) {
          hasCheckedStatusRef.current = true;
          try {
            const payload = {
              traceId: details.traceID,
              supplierParameter: details.supplierParameter,
              key: details.providerName,
              confirmationNo: details.confirmationNo,
              bookingRefId: details.bookingRefId,
            };
            console.log("Auto-updating latest booking status from supplier:", payload);
            await retrieveHotelBooking(payload);

            // Retrieve updated details from database
            let updatedResponse = await getBookingDetailById(refId);
            if (Array.isArray(updatedResponse) && updatedResponse.length > 0) {
              updatedResponse = updatedResponse[0];
            }
            let updatedDetails = null;
            if (updatedResponse && updatedResponse.booking) {
              updatedDetails = updatedResponse.booking;
            } else if (updatedResponse && updatedResponse.Booking) {
              updatedDetails = updatedResponse.Booking;
            } else if (updatedResponse && updatedResponse.pkid) {
              updatedDetails = updatedResponse;
            }

            if (updatedDetails) {
              setBookingDetails(updatedDetails);
              const updatedStatus = updatedDetails.bookingStatus?.toUpperCase();
              if (pendingStatuses.includes(updatedStatus)) {
                setModalStatus(updatedDetails.bookingStatus || "Pending");
                setShowStatusModal(true);
              }
              return updatedDetails;
            }
          } catch (retrieveError) {
            console.warn("Failed to automatically retrieve booking status:", retrieveError);
            setModalStatus(details.bookingStatus || "Pending");
            setShowStatusModal(true);
          }
        } else if (pendingStatuses.includes(status)) {
          setModalStatus(details.bookingStatus || "Pending");
          setShowStatusModal(true);
        }

        return details;
      } else {
        setError(`Booking details not found for ID: ${refId}.`);
      }
    } catch (err) {
      console.error("Error fetching hotel details:", err);
      setError(
        err.message ||
          "Failed to load hotel booking details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrintVoucher = () => {
    const url = `/reservations/hotel/PrintTicket?ReferenceNo=${bookingDetails?.bookingRefId}`;
    const printWindow = window.open(url, "_blank");
    if (printWindow) printWindow.focus();
  };
  const handleDownloadVoucher = () => {
    alert(
      "Please select 'Save as PDF' in the print dialog to download your voucher.",
    );
    handlePrintVoucher();
  };

  const handlePrintInvoice = () => {
    const url = `/reservations/hotel/PrintInvoice?ReferenceNo=${bookingDetails?.bookingRefId}`;
    const printWindow = window.open(url, "_blank");
    if (printWindow) printWindow.focus();
  };

  const handleBookHold = async () => {
    if (!window.confirm("Are you sure you want to proceed with Book Hold?"))
      return;
    try {
      setLoading(true);

      // 1. Collect Payment
      const paymentResponse = await CollectPayment({
        transactionID: bookingDetails.bookingRefId || "",
        referenceNo: bookingDetails.bookingRefId || "",
        corporateCode: bookingDetails.corporateCode,
        userId:bookingDetails.corporateTraveler,
        service: "Hotel",
        sessionId: bookingDetails.traceID || "",
        transactionType: "",
        remarks: "Book Hold",
        totalAmount: Number(bookingDetails.totalPrice || 0).toFixed(2),
        creditLimit: true,
        status: "",
        paymentMode: {
          paymentModeType: "",
          cardType: "",
          productType: "",
          pgProvider: "AgencyCreditLimit",
        },
      });

      if (
        paymentResponse?.success ||
        paymentResponse?.status === 1 ||
        paymentResponse?.transactionId
      ) {
        // 2. Hotel Book Hold
        const payload = {
          traceId: bookingDetails.traceID,
          supplierParameter: bookingDetails.supplierParameter,
          key: bookingDetails.providerName,
          confirmationNo: bookingDetails.confirmationNo,
          bookingRefId: bookingDetails.bookingRefId,
        };
        const response = await hotelBookHold(payload);
        if (
          response &&
          (response.status === true ||
            response.status === "true" ||
            response.status === 1 ||
            response.confirmationNo ||
            response.bookingStatus?.toLowerCase() === "booking confirmed" ||
            response.bookingStatus?.toLowerCase() === "success")
        ) {
          alert("Booking hold processed successfully!");
          fetchHotelBookingDetails(referenceNo);
        } else {
          alert(
            response?.message || "Booking hold failed. Please try again later.",
          );
        }
      } else {
        alert(paymentResponse?.message || "Payment collection failed.");
      }
    } catch (err) {
      console.error("Error in Book Hold:", err);
      alert(err.message || "An error occurred while processing Book Hold.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetStatus = async () => {
    try {
      setLoading(true);
      const payload = {
        traceId: bookingDetails.traceID,
        supplierParameter: bookingDetails.supplierParameter,
        key: bookingDetails.providerName,
        confirmationNo: bookingDetails.confirmationNo,
        bookingRefId: bookingDetails.bookingRefId,
      };
      await retrieveHotelBooking(payload);
      // After retrieving, refresh the booking details
      await fetchHotelBookingDetails(referenceNo);
    } catch (err) {
      console.error("Error retrieving booking status:", err);
      alert("Failed to retrieve the latest status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderPolicyInstructions = () => {
    const rawInstructions = bookingDetails.policy_Instructions || bookingDetails.policyInstructions || bookingDetails.policy_instructions;
    if (!rawInstructions) return null;

    try {
      const instructions = typeof rawInstructions === "string"
        ? JSON.parse(rawInstructions)
        : rawInstructions;

      if (!Array.isArray(instructions) || instructions.length === 0) return null;

      return (
        <div className="row mt-4">
          <div className="col-md-12">
            <h6 className="ruler">Policy & Instructions</h6>
          </div>
          <div className="col-md-12">
            <div className="row">
              {instructions.map((item, idx) => {
                let parsedMsg = item.msg;
                if (typeof item.msg === "string") {
                  try {
                    parsedMsg = JSON.parse(item.msg);
                  } catch (e) {
                    parsedMsg = item.msg;
                  }
                }

                const title = item.type ? item.type.replace(/_/g, " ") : "INFO";

                return (
                  <div key={idx} className="col-md-12 mb-3">
                    <h6 className="text-capitalize fw-bold" style={{ color: "#110942" }}>
                      {title}
                    </h6>
                    {typeof parsedMsg === "object" && parsedMsg !== null ? (
                      Object.entries(parsedMsg).map(([key, val]) => (
                        <div className="d-flex mb-2" key={key}>
                          <div className="labl" style={{ width: "200px" }}>
                            {key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                          </div>
                          <div className="labl_detls fw-bold">
                            {val}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="mb-1 text-muted">{parsedMsg}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error parsing policy instructions:", error);
      return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {loading && (
        <div className="d-flex justify-content-center align-items-center vh-100 position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75" style={{ zIndex: 9999 }}>
          <p className="text-center load"><span className="loader"></span></p>
        </div>
      )}
      <HeaderDashboard />
      <div className="toolbar py-2" id="kt_toolbar">
        <div
          id="kt_toolbar_container"
          className="container-fluid d-flex align-items-center"
        >
          <div className="flex-grow-1 flex-shrink-0 me-5">
            <div className="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
              <h1 className="d-flex align-items-center text-gray-900 fw-bold my-1 fs-3">
                Reservations
              </h1>
              <span className="h-20px border-gray-200 border-start mx-3"></span>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1">
                <li className="breadcrumb-item text-muted">Hotel</li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-200 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to="/Reservations/Hotel/AllTransactions"
                    className="text-muted text-hover-primary"
                  >
                    All Transactions
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-200 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-gray-900">
                  Transaction Details
                </li>
              </ul>
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <div className="flex-shrink-0 me-2 d-none-mob">
              <ul className="nav" role="tablist">
                <li className="nav-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.back();
                    }}
                  >
                    <img src="/assets/images/back.png" width={30} alt="Back" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <section className="wraper">
        <div className="box_shadow mt-14">
          {loading && (
            <div className="row mamage_depo">
              <div className="col-lg-12">
                <h6 className="ruler">Hotel Booking Details</h6>
                <p className="">Loading Hotel Booking Details...</p>
              </div>
            </div>
          )}

          {bookingDetails ? (
            <>
              <div className="row mamage_depo">
                <div className="col-lg-6">
                  <h6 className="ruler">Hotel Booking Details</h6>
                </div>
                {(bookingDetails.bookingStatus?.toUpperCase() === "CONFIRMED" ||
                  bookingDetails.bookingStatus?.toUpperCase() === "SUCCESS" ||
                  bookingDetails.bookingStatus?.toUpperCase() ===
                    "BOOKING CONFIRMED") && (
                  <div className="col-lg-6 text-right">
                    <ul className="option_s">
                      <li>
                        <button
                          type="button"
                          onClick={handlePrintVoucher}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          title="Print Voucher"
                        >
                          <img
                            src="/assets/images/printer.png"
                            alt="Print Voucher"
                          />
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={handleDownloadVoucher}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          title="Download Voucher"
                        >
                          <img
                            src="/assets/images/download.png"
                            alt="Download Voucher"
                          />
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={handlePrintInvoice}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          title="Invoice"
                        >
                          <img src="/assets/images/Invoice.png" alt="Invoice" />
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
                {["HOLD", "ON_HOLD"].includes(bookingDetails.bookingStatus?.toUpperCase()) && (
                  <div className="col-lg-6 text-end d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-sm me-2 fw-bold"
                      style={{
                        backgroundColor: "#1a1a3c",
                        color: "#f9c349",
                        border: "none",
                        padding: "8px 16px",
                      }}
                      onClick={handleBookHold}
                    >
                      Process Book Hold
                    </button>
                    <Link
                      to={`/reservations/hotel/CancellationBooking?ReferenceNo=${bookingDetails.bookingRefId}`}
                      className="btn btn-sm btn-danger fw-bold"
                      style={{ padding: "8px 16px", width: 160 }}
                    >
                      Cancel Booking
                    </Link>
                  </div>
                )}
                {[
                  "CANCELLATION_PENDING",
                  "PENDING",
                  "PAYMENT_PENDING",
                  "PAYMENT_SUCCESS",
                  "IN_PROGRESS",
                  "IN_PAYMENT",
                  "REQUESTED",
                  "APPROVE_PENDING"
                ].includes(bookingDetails.bookingStatus?.toUpperCase()) && (
                  <div className="col-lg-6 text-end d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-sm btn-primary fw-bold"
                      style={{ padding: "8px 16px" }}
                      onClick={handleGetStatus}
                    >
                      Get Status
                    </button>
                  </div>
                )}
              </div>

              <div className="col-md-12 col-lg-12">
                <table className="table table-bordered responsive-table">
                  <thead>
                    <tr>
                      <th>Reference No</th>
                      <th>Confirmation Number</th>
                      <th>Booking Status</th>
                      <th>Booking Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{bookingDetails.bookingRefId}</td>
                      <td>{bookingDetails.confirmationNo}</td>
                      <td>
                        {(() => {
                          const status = (bookingDetails.bookingStatus || "").toUpperCase();
                          let badgeClass = "bg-secondary";
                          if (status === "CONFIRMED" || status === "SUCCESS" || status === "BOOKING CONFIRMED") badgeClass = "bg-success";
                          else if (["PENDING", "HOLD", "ON_HOLD", "CANCELLATION_PENDING", "APPROVE_PENDING"].includes(status)) badgeClass = "bg-warning";
                          else if (["FAILED", "CANCELLED", "APPROVE_REJECTED"].includes(status)) badgeClass = "bg-danger";
                          return (
                            <span className={`badge ${badgeClass} text-white fw-bold px-3 py-1.5`} style={{ textTransform: "uppercase" }}>
                              {status || "-"}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {new Date(bookingDetails.createdAt).toLocaleString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="col-md-12 col-lg-12 mt-4">
                <h6 className="ruler">Hotel & Stay Information</h6>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Hotel Name</th>
                        <th>City / Address</th>
                        <th>Check-In</th>
                        <th>Check-Out</th>
                        <th>Nights / Rooms</th>
                      </tr>
                      <tr>
                        <td className="fw-bold text-primary">
                          {bookingDetails.hotelName}
                        </td>
                        <td>
                          {bookingDetails.cityName}
                          <br />
                          <small className="text-muted">
                            {bookingDetails.hotelAddress}
                          </small>
                        </td>
                        <td>
                          {formatDate(bookingDetails.checkInDate)}
                          <br />
                          <small className="fw-bold">
                            {bookingDetails.checkInTime}
                          </small>
                        </td>
                        <td>
                          {formatDate(bookingDetails.checkOutDate)}
                          <br />
                          <small className="fw-bold">
                            {bookingDetails.checkOutTime}
                          </small>
                        </td>
                        <td>
                          {bookingDetails.noOfNights} Night(s) /{" "}
                          {bookingDetails.noOfRooms} Room(s)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {(bookingDetails.bookingStatus?.toUpperCase() === "CONFIRMED" ||
                bookingDetails.bookingStatus?.toUpperCase() === "SUCCESS" ||
                bookingDetails.bookingStatus?.toUpperCase() ===
                  "BOOKING CONFIRMED") && (
                <div className="col-md-12 col-lg-12 mb-12 mt-4">
                  <h6 className="ruler">Booking Amendments</h6>
                  <div className="row g-4">
                    <div className="col-md-3">
                      <div className="box_shadows text-left">
                        <div
                          onClick={handlePrintVoucher}
                          style={{ cursor: "pointer" }}
                        >
                          <h5>
                            <img src="/assets/images/printer.png" alt="Print" />{" "}
                            Print Voucher
                          </h5>
                          <p>Print your hotel voucher for check-in purposes.</p>
                          <button className="btn-raise">Print Voucher</button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="box_shadows">
                        <div
                          onClick={handlePrintInvoice}
                          style={{ cursor: "pointer" }}
                        >
                          <h5>
                            <img
                              src="/assets/images/Invoice.png"
                              alt="Invoice"
                            />{" "}
                            Invoice
                          </h5>
                          <p>View and download the invoice for this booking.</p>
                          <button className="btn-raise">View Invoice</button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="box_shadows">
                        <div
                          onClick={handleDownloadVoucher}
                          style={{ cursor: "pointer" }}
                        >
                          <h5>
                            <img
                              src="/assets/images/download.png"
                              alt="Download"
                            />{" "}
                            Download Voucher
                          </h5>
                          <p>Download a PDF copy of your hotel stay voucher.</p>
                          <button className="btn-raise">Download</button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="box_shadows">
                        <a
                          href=""
                          data-bs-toggle="modal"
                          data-bs-target="#EmailHotelTicket"
                        >
                          <h5>
                            <img src="/assets/images/email.png" alt="Email" />{" "}
                            Email Voucher
                          </h5>
                          <p>
                            Send a copy of the voucher to your email address.
                          </p>
                          <button className="btn-raise">Email Voucher</button>
                        </a>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="box_shadows">
                        <a
                          href=""
                          data-bs-toggle="modal"
                          data-bs-target="#SMSHotelTicket"
                        >
                          <h5>
                            <img src="/assets/images/sms.png" alt="SMS" /> SMS
                            Details
                          </h5>
                          <p>
                            Send booking details to your mobile number via SMS.
                          </p>
                          <button className="btn-raise">SMS Details</button>
                        </a>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="box_shadows">
                        <a
                          href={`/reservations/hotel/CancellationBooking?ReferenceNo=${bookingDetails.bookingRefId}`}
                        >
                          <h5>
                            <img src="/assets/images/ticket.png" alt="Cancel" />{" "}
                            Cancel Booking
                          </h5>
                          <p>
                            Check cancellation charges and request a refund.
                          </p>
                          <button className="btn-raise">Raise Request</button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="col-md-12 col-lg-12 mt-4">
                <h6 className="ruler">Cancellation Policy</h6>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="bg-light">
                      <tr>
                        <th>From Date</th>
                        <th>To Date</th>
                        <th>Cancellation Charges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let policies = bookingDetails.cancellationPolicy || [];
                        if (policies.length === 0) {
                          bookingDetails.rooms?.forEach((room) => {
                            if (room.cancellationPolicyJson) {
                              try {
                                const parsed = JSON.parse(
                                  room.cancellationPolicyJson,
                                );
                                if (Array.isArray(parsed)) {
                                  policies = [...policies, ...parsed];
                                } else {
                                  policies.push(parsed);
                                }
                              } catch (e) {
                                console.error(
                                  "Error parsing cancellationPolicyJson",
                                  e,
                                );
                              }
                            }
                          });
                        }

                        if (policies.length === 0) {
                          return (
                            <tr>
                              <td
                                colSpan="3"
                                className="text-center text-muted"
                              >
                                No cancellation policy details available.
                              </td>
                            </tr>
                          );
                        }

                        return policies.map((policy, index) => (
                          <tr key={index}>
                            <td>
                              {policy.fromDate
                                ? new Date(policy.fromDate).toLocaleDateString(
                                    "en-GB",
                                  )
                                : "-"}
                            </td>
                            <td>
                              {policy.toDate
                                ? new Date(policy.toDate).toLocaleDateString(
                                    "en-GB",
                                  )
                                : "-"}
                            </td>
                            <td className="fw-bold text-danger">
                              {policy.chargeType === "Fixed" ||
                              policy.chargeType === "Amount"
                                ? bookingDetails.currency || policy.currency
                                : ""}{" "}
                              {(
                                policy.chargeValue ||
                                policy.penaltyAmount ||
                                0
                              ).toLocaleString()}{" "}
                              {policy.chargeType === "Percentage" ? "%" : ""}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              

              <div className="row mt-4">
                <div className="col-md-12">
                  <h6 className="ruler">Travel Policy</h6>
                </div>
                <div className="col-md-6">
                  <div className="d-flex mb-3">
                    <div className="labl">Policy Status</div>
                    <div
                      className="labl_detls fw-bold"
                      style={{
                        color:
                          bookingDetails.policyType === "OutOfPolicy"
                            ? "red"
                            : "green",
                      }}
                    >
                      {bookingDetails.policyType || "In Policy"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-md-12">
                  <h6 className="ruler">Contact Information</h6>
                </div>
                <div className="col-md-6">
                  <div className="d-flex mb-3">
                    <div className="labl">Email Address</div>
                    <div className="labl_detls fw-bold">
                      {bookingDetails.rooms?.[0]?.paxDetails?.find(
                        (p) => p.isLeadGuest,
                      )?.email ||
                        bookingDetails.rooms?.[0]?.paxDetails?.[0]?.email ||
                        "-"}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex mb-3">
                    <div className="labl">Mobile Number</div>
                    <div className="labl_detls fw-bold">
                      {bookingDetails.rooms?.[0]?.paxDetails?.find(
                        (p) => p.isLeadGuest,
                      )?.phone ||
                        bookingDetails.rooms?.[0]?.paxDetails?.[0]?.phone ||
                        "-"}
                    </div>
                  </div>
                </div>
              </div>


              <div className="col-md-12 col-lg-12 mt-4">
                <h6 className="ruler">Passenger & Room Details</h6>
                {bookingDetails.rooms?.map((room, index) => {
                  const isRefundable = room.refundable || bookingDetails.rooms?.[0]?.refundable;
                  return (
                    <div
                      key={index}
                      className="mb-4 shadow-sm border rounded p-3 bg-white"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h5 className="mb-0 text-primary">
                          Room {index + 1}: {room.roomName}
                        </h5>
                        <span
                          className={`badge ${isRefundable ? "bg-success" : "bg-danger"}`}
                        >
                          {isRefundable ? "Refundable" : "Non-Refundable"}
                        </span>
                      </div>
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <span className="text-muted small d-block">
                          Meal Basis
                        </span>
                        <span className="fw-bold">
                          {room.mealBasis || "N/A"}
                        </span>
                      </div>
                      <div className="col-md-4">
                        <span className="text-muted small d-block">
                          Occupancy
                        </span>
                        <span className="fw-bold">
                          {room.noOfAdults} Adult(s), {room.noOfChildren}{" "}
                          Child(ren)
                        </span>
                      </div>
                      {/* <div className="col-md-4 text-end">
                        <span className="text-muted small d-block">
                          Room Price
                        </span>
                        <span className="fw-bold">
                          {bookingDetails.currency}{" "}
                          {room.roomPrice?.toLocaleString()}
                        </span>
                      </div> */}
                    </div>

                    <div className="table-responsive">
                      <table className="table table-bordered table-sm">
                        <thead className="bg-light">
                          <tr>
                            <th>Name</th>
                            <th>Type</th>

                            <th>PAN</th>
                            {room.paxDetails?.some((p) => p.passportNumber) && (
                              <>
                                <th>Passport No</th>
                                <th>Passport Expiry</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {room.paxDetails?.map((pax, pIdx) => (
                            <tr key={pIdx}>
                              <td className="fw-bold">
                                {pax.title}. {pax.firstName} {pax.lastName}{" "}
                                {pax.isLeadGuest && (
                                  <span className="badge bg-info ms-1">
                                    Lead
                                  </span>
                                )}
                              </td>
                              <td>{pax.paxType}</td>

                              <td>{pax.panNumber || "-"}</td>
                              {room.paxDetails?.some(
                                (p) => p.passportNumber,
                              ) && (
                                <>
                                  <td>{pax.passportNumber || "-"}</td>
                                  <td>
                                    {pax.passportExpiry
                                      ? formatDate(pax.passportExpiry)
                                      : "-"}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              </div>

              <div className="col-md-12 col-lg-12 mt-4">
                <h6 className="ruler">Payment Information (Fare Summary)</h6>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="bg-light">
                      <tr>
                        <th>Room Price</th>
                        <th>Taxes (incl. GST)</th>
                        {/* {bookingDetails.gstAmount > 0 && <th>GST (Inclusive)</th>} */}
                        <th>Other Charges</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {bookingDetails.currency}{" "}
                          {Math.round(
                            bookingDetails.basePrice
                          ).toLocaleString()}
                        </td>
                        <td>
                          {bookingDetails.currency}{" "}
                          {Math.round(
                            bookingDetails.totalTax +
                            bookingDetails.managementFee+
                            bookingDetails.managementFeeTax
                          ).toLocaleString()}
                        </td>
                        {/* {bookingDetails.gstAmount > 0 && (
                          <td>
                            {bookingDetails.currency}{" "}
                            {Math.round(bookingDetails.gstAmount).toLocaleString()}
                          </td>
                        )} */}
                        <td>
                          {bookingDetails.currency}{" "}
                          {Math.round(
                            bookingDetails.otherCharges || 0
                          ).toLocaleString()}
                        </td>
                        <th className="fs-5 text-primary">
                          {bookingDetails.currency}{" "}
                          {Math.round(bookingDetails.totalPrice || 0).toLocaleString()}
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {renderPolicyInstructions()}
            </>
          ) : (
            !loading && (
              <div className="p-5 text-center">
                <h5 className="text-muted">
                  {error || "No Hotel Booking Details Found"}
                </h5>
                <Link
                  to="/Reservations/Hotel/AllTransactions"
                  className="btn btn-primary mt-3"
                >
                  Back to Transactions
                </Link>
              </div>
            )
          )}
        </div>
      </section>

      {/* Modals for Email/SMS/WhatsApp (Placeholders) */}
      <div className="modal fade" id="EmailHotelTicket" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Send Voucher to Email</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body">
              <HotelEmailTicket
                referenceNo={bookingDetails?.bookingRefId}
                traceID={bookingDetails?.traceID}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="SMSHotelTicket" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Send Details by SMS</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body">
              <SmsHotelTicket />
            </div>
          </div>
        </div>
      </div>
      {showStatusModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Booking Status Update</h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStatusModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center p-5">
                <div className="mb-4">
                  <span className="badge bg-warning fs-5 px-3 py-2 text-dark">{modalStatus}</span>
                </div>
                <p className="fs-5 fw-semibold mb-2">
                  The booking status is currently <strong>{modalStatus}</strong>.
                </p>
                <p className="text-muted mb-0">
                  Please click <strong>Get Status</strong> again after 10 minutes to check for updates.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStatusModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionsDetails;
